import React from 'react';
import _ from 'lodash';
import {
  formatAiredDate,
  formatDurationInSeconds,
  formatMetadata,
  formatMovieMetadata,
  formatRating,
  formatSubRatings,
  getItemBadge,
} from 'helpers/VideoHelper';
import { isEpgv2Enabled } from 'helpers/ConfigHelper';
import {
  FOX,
  getNetworkLogoUrl,
  getDisplayBrand,
  isStaticRender,
  sportsFeatures,
  checkContentLabel,
  isSportsContent,
  getRequiresAuth,
  TAB_PRIMETIME,
  TAB_SCHEDULE,
} from 'helpers/ContentHelper';
import {
  FORMAT_TIME,
  FORMAT_WEEKDAY_MONTH_DAY,
  FORMAT_MONTH_DAY,
  FORMAT_DAY_OF_WEEK,
  now,
  format,
  formatToAPIRange,
  formatToTwoDigitsString,
  formatToTwoDigitsTimezone,
  getTimeZoneAbbr,
  startOfDay,
  addMinutes,
  addHours,
  addDays,
  endOfDay,
  getDayOfYear,
  guessTZ,
  stringToDate,
  isAfter,
  isBetween,
  isBefore,
  isSameDay,
  isSameSecond,
  differenceInSeconds,
  differenceInMinutes,
  setMinutes,
} from 'helpers/DateTimeHelper';
import { helperGetRelativeUrl } from 'helpers/RouteHelper';
import { getLiveStatusBadge, SHOW_STATE_REPLAY } from 'helpers/BadgeHelper';
import { getChannelId } from 'components/VideoContainer/VideoContainerHelper';
import DropdownIcon from 'components/DropdownIcon';
import { RESTART_TYPE_LIVE } from 'screens/Watch/WatchHelpers';
import dropdownIconTheme from 'screens/Live/styles/DropdownIcon.css';

export const PANEL_TYPE_LISTING = 'listing';
const TYPE_MOVIE = 'movie';
export const TYPE_SHOW = 'fullEpisode';
const HALF_HOUR_MINUTES = 30;
const WIDTH_HALF_HOUR = 252;
export const MINUTES_INTERVAL = 30;
export const CHANNEL_PRESELECT = 'channel';

const GRID_COUNT = 4;
const BADGE_NEW = 'NEW';
const BADGE_SERIES_PREMIERE = 'SERIES PREMIERE';

export const EPG_TOTAL_DAYS = 7;

const primetimeNetworks = [FOX];

const getLocalNetworkUrl = (network, callSign) => {
  if (!callSign || (network && network !== FOX)) return null;
  return window.ExternalConfig.affiliateLogo.replace('{callSign}', callSign);
};

export const epgLinks = _.get(window.ExternalConfig, 'epgLinks', {
  tabOrder: [TAB_PRIMETIME, TAB_SCHEDULE],
  defaultTab: TAB_PRIMETIME,
  navLinkTab: TAB_SCHEDULE,
  scheduleLinkTab: TAB_PRIMETIME,
});

export const getNetworkLogo = ({ callSign, network = FOX, isStatic }) => {
  if (!callSign) return null;
  const channel = callSign.toLowerCase();

  const { networkLogo, epgNetworkLogoMapping } = window.ExternalConfig;

  let channelName = _.get(epgNetworkLogoMapping, network, null);
  if (!isStaticRender() && !isStatic) {
    channelName = _.get(epgNetworkLogoMapping, channel, null);
  }

  const channelLogo = networkLogo.replace('{network}', channelName);
  const affiliateLogo = getLocalNetworkUrl(network, callSign);

  const networkLogoUrl = channelName ? channelLogo : affiliateLogo;

  // append akamai resize params.
  const defaultSize = 60;
  if (!networkLogoUrl) return null;
  return `${networkLogoUrl}?resize=*:${defaultSize}px`;
};

const getTs = () => Math.floor(new Date().getTime() / 1000);

export const getTsMinutes = () => Math.floor(getTs() / 60);

// TODO: Remove this function when EPG Schedule is no longer used
export const getHourInterval = (selectedDate, interval = MINUTES_INTERVAL, count = GRID_COUNT) => {
  let current = stringToDate(selectedDate);
  const hourIntervals = [];
  for (let i = 0; i < count; i += 1) {
    const minute = format(current, 'mm');

    const currentDate = format(current, 'MM/DD');
    const currentHour = format(current, 'h');
    const currentAmPM = format(current, 'a');
    const currentMinute = i === 0 ? minute - (minute % interval) : minute;

    current = setMinutes(current, currentMinute);

    const minuteStr = formatToTwoDigitsString(currentMinute);

    const hour = `${currentHour}${currentAmPM}`;
    const hourMinute = `${currentHour}:${minuteStr}${currentAmPM}`;
    const displayMinutes = currentMinute !== 0;
    const timeString = displayMinutes ? hourMinute : hour;

    const dateTimeString = `${currentDate}@${timeString}`;

    hourIntervals.push({ timeString, dateTimeString, current });

    current = addMinutes(current, interval);
  }

  return hourIntervals;
};

export const getStartingTs = current => {
  const currentDate = current ? stringToDate(current) : now();
  const hourIntervals = getHourInterval(currentDate, MINUTES_INTERVAL, 1);
  const { current: currentInterval } = hourIntervals[0];
  return Math.round(currentInterval.getTime() / 1000);
};

export const calculateDurationWidth = duration => {
  const halfHours = Math.round(duration / HALF_HOUR_MINUTES);
  const padding = halfHours > 1 ? (halfHours - 1) * 20 : 0;
  return (duration * WIDTH_HALF_HOUR) / HALF_HOUR_MINUTES + padding;
};

export const getVideoTimeData = ({
  video,
  current = null,
  gridTotalHours = null,
  canScrollGrid = false,
  breakpointIsMobile = false,
}) => {
  if (!video) {
    return null;
  }
  const currentDate = current || now();
  const totalHours = gridTotalHours || 2;
  const { startDate, endDate } = video;

  const starts = startDate ? stringToDate(startDate) : currentDate;
  const ends = endDate ? stringToDate(endDate) : currentDate;

  const startOfCurrentDay = startOfDay(currentDate);
  const endOfGrid =
    isEpgv2Enabled() && breakpointIsMobile
      ? addHours(startOfCurrentDay, totalHours)
      : addHours(currentDate, totalHours);
  const endOfNextDay = addDays(endOfDay(currentDate), 1);

  const startFormated = format(starts, FORMAT_TIME);
  const endFormated = format(ends, FORMAT_TIME);

  const duration = differenceInMinutes(ends, starts);

  const startsInFuture = isAfter(starts, currentDate);
  const isInFutureAndDateRange =
    isAfter(currentDate, endOfDay(now())) && isBetween(starts, startOfCurrentDay, endOfNextDay);

  const startsWithGrid =
    (isInFutureAndDateRange && !canScrollGrid) || isSameSecond(currentDate, starts);

  const isCurrent = startsWithGrid || isBetween(currentDate, starts, ends);
  const isPast = isAfter(currentDate, ends);

  const shouldBeVisible =
    startsWithGrid || isCurrent || (startsInFuture && !isAfter(starts, endOfGrid));
  const withinSameDay = isSameDay(starts, currentDate);

  const secUntilStart = startsInFuture
    ? Math.floor(differenceInSeconds(starts, currentDate))
    : null;

  return {
    startDate,
    endDate,
    starts: startFormated,
    ends: endFormated,
    duration,
    startsInFuture,
    isCurrent,
    isPast,
    shouldBeVisible,
    withinSameDay,
    startsWithGrid,
    secUntilStart,
  };
};

export const isGapFiller = video => _.get(video, 'showCode') === 'gapFiller';

export const isVideoLive = video => {
  if (isGapFiller(video)) return false;

  const startDate = _.get(video, 'startDate', null);
  const endDate = _.get(video, 'endDate', null);

  const start = startDate ? stringToDate(startDate) : null;
  const end = endDate ? stringToDate(endDate) : null;

  const result = start && end ? isBetween(now(), start, end) : false;

  return result;
};

export const getLiveVideo = ({ panel, isOverFlow, upNext, epgListingId = null }) => {
  const upNextItems = _.get(upNext, 'member', null);
  const items = _.get(panel, 'items.member', upNextItems);
  if (!items) return null;
  const liveItems = items.filter(item => isVideoLive(item));
  let epgItem = [];
  if (epgListingId) {
    epgItem = _.filter(liveItems, { id: epgListingId });
  }
  const nonOverFlowItems = epgItem[0] || liveItems[0];
  return isOverFlow ? liveItems[1] : nonOverFlowItems;
};

export const checkSameDayOfYear = (oldDate, newDate) => {
  const oldDateTmp = oldDate || now();
  const newDateTmp = newDate || now();

  const oldDay = getDayOfYear(oldDateTmp);
  const newDay = getDayOfYear(newDateTmp);

  const isSameDay = oldDay === newDay;
  return isSameDay;
};

const formatDuration = durationInSeconds => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds - hours * 3600) / 60);
  let duration = hours > 0 ? `${hours}h` : '';
  if (minutes > 0) duration += `${minutes}m`;
  return duration;
};

export const getVideoData = (video, dontRetrieveNetworkLogo = false) => {
  if (!video) {
    return null;
  }

  const {
    '@id': id,
    images,
    livePlayerScreenUrl,
    name,
    secondaryTitle,
    seriesName,
    seriesScreenUrl,
    originalAirDate,
    contentRating,
    subRatings,
    description: originalDescription,
    network,
    startDate,
    endDate,
    videoType,
    fullEpisodeCount,
    playerScreenUrl,
    callSign,
    contentLabel,
    sportTag,
    durationInSeconds,
  } = video;

  const requiresAuthLive = getRequiresAuth({ video, isLive: true });
  const channel = getChannelId({ network });
  const networks = [channel];
  const { programNotAvailable_text: dummyTitle } = window.Language;
  const starts = startDate ? format(stringToDate(startDate), FORMAT_TIME) : null;
  const ends = endDate ? format(stringToDate(endDate), FORMAT_TIME) : null;

  const isLiveNow = isVideoLive(video);

  const fallbackImage = _.get(images, 'seriesList.raw', null);
  const image = _.get(images, 'videoList.raw', fallbackImage);
  let title = seriesName;
  let airDate = formatAiredDate(originalAirDate);
  const rating = formatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingText = !rating && !subRating ? null : `${rating} ${subRating}`;
  const moreInfoUrl = helperGetRelativeUrl({ url: seriesScreenUrl });
  const hashCallSign = callSign ? `-${callSign}` : '';
  const watchUrl = helperGetRelativeUrl({
    url: playerScreenUrl,
    hash: `${RESTART_TYPE_LIVE}${hashCallSign}`,
  });
  const displayBrand = getDisplayBrand(video);
  const networkIcon = !dontRetrieveNetworkLogo && getNetworkLogoUrl(displayBrand);
  let seasonAndEpisode = null;
  let subtitle = null;
  let metadata = null;
  let description = null;
  let upNext = false;

  let badge = getItemBadge(video);
  if (badge) badge = badge.toUpperCase();

  // If it's not NEW then ONLY show 'Replay'
  if (badge !== BADGE_NEW && badge !== BADGE_SERIES_PREMIERE && sportsFeatures()) {
    badge = getLiveStatusBadge(video.contentLabel);
    if (badge !== SHOW_STATE_REPLAY) badge = null;
  }

  switch (videoType) {
    case TYPE_SHOW: {
      const { seasonNumber, episodeNumber } = video;
      subtitle =
        seasonNumber && episodeNumber
          ? `S${seasonNumber} E${episodeNumber} ${secondaryTitle || ''}`
          : secondaryTitle;
      seasonAndEpisode = seasonNumber && episodeNumber ? `S${seasonNumber} E${episodeNumber}` : '';
      airDate = badge === BADGE_NEW ? formatAiredDate(startDate, '', FORMAT_TIME) : airDate;
      metadata = formatMetadata([airDate, ratingText]);
      description = originalDescription;
      break;
    }
    case TYPE_MOVIE:
      subtitle = starts;
      metadata = formatMovieMetadata(video);
      description = originalDescription;
      break;
    default:
      subtitle = name;
      break;
  }

  if (isSportsContent(video)) {
    subtitle = sportTag || starts;
    description = originalDescription;
    const labels = ['Live', 'Up Next'];

    let isBeforeOriginalAirDate = false;
    if (originalAirDate && startDate) {
      isBeforeOriginalAirDate = isBefore(stringToDate(originalAirDate), stringToDate(startDate));
    }

    airDate =
      checkContentLabel({ video, labels }) ||
      !originalAirDate ||
      (originalAirDate && !isBeforeOriginalAirDate)
        ? formatAiredDate(startDate, '', FORMAT_TIME)
        : airDate;
    const duration = formatDuration(durationInSeconds);
    metadata = formatMetadata([airDate, ratingText, duration]);
    upNext = contentLabel && _.includes(contentLabel, 'Up Next') ? true : upNext;
  }

  if (!title) {
    if (subtitle) {
      title = subtitle;
      subtitle = null;
    } else {
      title = dummyTitle;
    }
  }

  const shouldRenderLiveCta = livePlayerScreenUrl && isLiveNow;
  const isTimeShiftedLiveRestart = _.get(video, 'timeShiftedLiveRestart', true);
  const hideVODCta = isTimeShiftedLiveRestart && !isLiveNow;
  const shouldRenderVODCta = watchUrl && livePlayerScreenUrl && !hideVODCta;

  const videoData = {
    badge,
    callSign,
    description,
    endDate,
    ends,
    fullEpisodeCount,
    id,
    image,
    isLiveNow,
    metadata,
    moreInfoUrl,
    networkIcon,
    network,
    networks,
    playerScreenUrl,
    shouldRenderLiveCta,
    shouldRenderVODCta,
    requiresAuthLive,
    seasonAndEpisode,
    subtitle,
    subtitleName: name,
    startDate,
    starts,
    title,
    upNext,
    videoType,
    watchUrl,
  };

  return videoData;
};

export const getMobileVideoMetadata = video => {
  const {
    startDate,
    endDate,
    originalAirDate,
    available,
    datePublished,
    durationInSeconds,
    contentRating,
    subRatings,
    contentLabel,
  } = video;

  const dateDiff = differenceInSeconds(stringToDate(endDate), stringToDate(startDate));
  const isNew = _.lowerCase(_.get(contentLabel, '0')) === 'new';
  const primaryAirDate = isNew ? startDate : originalAirDate;
  const airDate = formatAiredDate(primaryAirDate || datePublished || available, '', 'MM-DD-YY');
  const duration = formatDurationInSeconds(durationInSeconds || dateDiff);
  const rating = formatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  let metadata = [];
  if (airDate) metadata.push(airDate);
  if (ratingStr) metadata.push(ratingStr);
  if (duration) metadata.push(duration);
  metadata = formatMetadata(metadata);
  return metadata;
};

const mapDayToPrimeTimeMessage = day => {
  const {
    epg_prePrimetimeText_Monday: textMonday,
    epg_prePrimetimeText_Tuesday: textTuesday,
    epg_prePrimetimeText_Wednesday: textWednesday,
    epg_prePrimetimeText_Thursday: textThursday,
    epg_prePrimetimeText_Friday: textFriday,
    epg_prePrimetimeText_Saturday: textSaturday,
    epg_prePrimetimeText_Sunday: textSunday,
    epg_prePrimetimeText: textDefault,
  } = window.Language;
  switch (day) {
    case 'Monday':
      return textMonday;
    case 'Tuesday':
      return textTuesday;
    case 'Wednesday':
      return textWednesday;
    case 'Thursday':
      return textThursday;
    case 'Friday':
      return textFriday;
    case 'Saturday':
      return textSaturday;
    case 'Sunday':
      return textSunday;
    default:
      return textDefault;
  }
};

export const getPrimeTimeMessage = ({ panels, selectedChannel, selectedDate }) => {
  const now = stringToDate(selectedDate);
  const day = format(now, FORMAT_DAY_OF_WEEK);

  const selectedChannelPanel = _.get(panels, `member.${selectedChannel}`, null);
  const firstPanel = _.get(panels, 'member.0', null);

  const panel = selectedChannel ? selectedChannelPanel : firstPanel;
  const primeTimeMessage = mapDayToPrimeTimeMessage(day);
  return _.get(panel, 'additionalInformation', primeTimeMessage);
};

export const displayPrimeTimeMessage = video => {
  const network = _.get(video, 'network', null);
  return network === FOX;
};

export const getPrimeTimeNetworkFilter = () => _.join(primetimeNetworks, ',');

export const isInEPGDateRange = date => {
  if (!date) return false;
  const startDate = stringToDate(date);
  const today = startOfDay(now());
  const epgEndDate = endOfDay(addDays(now(), EPG_TOTAL_DAYS));
  const result = isBetween(startDate, today, epgEndDate);
  return result;
};

export const isToday = date => {
  if (!date) return false;
  return isSameDay(stringToDate(date), now());
};

const isTomorrow = dateArg => {
  const daysToAdd = 1;
  const date = stringToDate(dateArg);
  const tomorrow = addDays(now(), daysToAdd);
  const result = isSameDay(date, tomorrow);
  return result;
};

export const getDayLabel = date => {
  const { epg_todayHeader: todayLabel, epg_tomorrowHeader: tomorrowLabel } = window.Language;
  if (isToday(date)) return todayLabel;
  if (isTomorrow(date)) return tomorrowLabel;
  return null;
};

export const getTimezone = () => {
  let timezone = guessTZ();
  if (!timezone || timezone.length < 3) {
    timezone = 'America/Los_Angeles';
  }
  const abbr = getTimeZoneAbbr(now(), 'z', timezone);
  const shortAbbr = formatToTwoDigitsTimezone(abbr);
  return shortAbbr || '';
};

export const checkForOverlap = panel => {
  const listings = _.get(panel, 'items.member', null);
  let overFlowProgramIdx = -1;
  let lateProgramIdx = -1;
  let overFlowPrograms = [];
  if (!listings || listings.length === 1) return { lateProgramIdx, overFlowProgramIdx };
  const liveListings = listings.filter(listing => isVideoLive(listing) === true);
  if (liveListings.length > 1) {
    const lateProgram = liveListings[0];
    const overFlowProgram = liveListings[1];
    overFlowPrograms = liveListings.splice(1);
    lateProgramIdx = _.findIndex(listings, lateProgram);
    overFlowProgramIdx = _.findIndex(listings, overFlowProgram);
  }
  return { lateProgramIdx, overFlowProgramIdx, overFlowPrograms };
};

export const getDateDropdownFormat = isCondensed =>
  isCondensed ? FORMAT_WEEKDAY_MONTH_DAY : FORMAT_MONTH_DAY;

export const getDateDropdownIcon = isOpen => onClick =>
  isOpen ? null : <DropdownIcon theme={dropdownIconTheme} onClick={onClick} opened={isOpen} />;

export const getTodaysDate = () => format(now(), FORMAT_WEEKDAY_MONTH_DAY);

export const isLastPanel = (rowIdx, panels) => {
  if (!panels || !panels.member || panels.length === 0) return null;
  return rowIdx === panels.member.length - 1;
};

export const isReturningVistor = location => _.get(location, 'action', null) === 'POP';

export const parseLiveVideoResponse = data => {
  const liveVideo = data;
  const panel = data;

  return { liveVideo, panel };
};

const getEndDate = ({ startDate, days = 0, hours = 0 }) =>
  addHours(addDays(startOfDay(startDate), days), hours);

const getTwoDayInterval = selectedDate => {
  if (!selectedDate) return null;

  const daysToAdd = 1;
  const hoursToAdd = 23;

  const startDate = startOfDay(selectedDate);
  const endDate = getEndDate({ startDate, hours: hoursToAdd, days: daysToAdd });
  const range = formatToAPIRange(startDate, endDate);

  return range;
};

export const getLiveDateRange = selectedDate => getTwoDayInterval(selectedDate);

export const getSingleStationSchedulePanels = (panels, isFNCV2, isNationDigitalChannel) => {
  if (!panels) return null;
  const panelSchedule = {
    ...panels,
    member: [],
  };
  const member = _.get(panels, 'member', []);
  member.forEach((panel, index) => {
    const items = _.get(panel, 'items', {});
    const videos = _.get(items, 'member', []);
    const now = new Date();
    const tomorrow = addHours(now, 24);
    let upcomingVideos = videos;
    if (!isNationDigitalChannel && index === 0) {
      upcomingVideos = _.filter(videos, video =>
        isFNCV2
          ? video.startDate && isAfter(video.startDate, now) && isBefore(video.startDate, tomorrow)
          : video.startDate && isAfter(video.startDate, now),
      );
    }
    panelSchedule.member.push({
      ...panel,
      items: {
        ...items,
        member: upcomingVideos,
      },
    });
  });
  return panelSchedule;
};

export const getCallSignName = callSign => {
  switch (callSign) {
    case 'NATION-DIGITAL':
      return 'FOX NATION CHANNEL';
    case 'FNC':
      return 'FOX NEWS CHANNEL';
    default:
      return callSign;
  }
};
